import logo from '@/assets/images/ip_vietnam_emblem.svg'
import { ROUTER } from '@/shared/config'
import { useTheme } from '@/shared/context'
import { childrenOfActive, topKeyOfSelected, topLevelItems } from '@/shared/lib/menu-layout'
import { UiBreadcrumb, UiFloatButton, UiLayout, UiTooltip } from '@/shared/ui'
import { useLayoutSettings } from '@/shared/ui'
import { MenuUnfoldOutlined } from '@/shared/ui/icons'
import {
  PanelLeftCloseOutlined as PanelLeftClose,
  MenuUnfoldOutlined as PanelLeftOpen,
} from '@/shared/ui/icons'
import LinkCusstom from '@/shared/ui/link-custom'
import UiMenu from '@/shared/ui/ui-menu'
// ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
import { Button } from 'antd'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import HeaderMain from '../Header'
import MenuLeft from '../MenuLeft'
import { UserMenuStyled } from './styled'

const { Sider, Content } = UiLayout

interface LayoutAdminProps {
  children?: React.ReactNode
  selectedKey?: string[]
  menuAdmin?: any[]
  onClickMenu?: (menu: any) => void
  /** Chuỗi tổ tiên trong cây menu tới trang hiện tại. Rỗng → không vẽ breadcrumb. */
  breadcrumbItems?: { label: string; path?: string }[]
  /** Trạng thái thu gọn ban đầu của sidebar (nếu không truyền sẽ tự động thu gọn ở breakpoint lg) */
  defaultCollapsed?: boolean
}

/**
 * Logo + tên hệ thống. Ở `side` nó nằm đầu Sider; ở `top` / `mix` nó chuyển lên header —
 * giống antd pro: `GlobalHeader` vẽ `renderLogoAndTitle` khi layout là `mix`, còn
 * `TopNavHeader` (layout `top`) luôn có logo bên trái.
 */
const LogoBlock = ({
  compact,
  collapsed,
}: {
  compact?: boolean
  collapsed?: boolean
}) => (
  <Link
    to={ROUTER.HOME}
    // ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
    className={`d-flex pointer p-3 align-items-center`}
    style={
      compact
        ? {
            height: '100%',
            justifyContent: 'flex-start',
            padding: 0,
            marginRight: 16,
            gap: 8,
          }
        : {
            height: '56px',
            borderBottom: '1px solid var(--vbdt-color-border-subtle)',
            justifyContent: collapsed ? 'center' : 'flex-start',
            // Đo trên máy: icon mục menu ở x=28 (rộng 16 → tâm 36), nhãn ở x=56.
            // Logo 28px đặt tại x=22 nên tâm cũng là 36, chữ bắt đầu 22+28+6 = 56 —
            // logo thẳng cột với icon, tên thẳng cột với nhãn menu.
            padding: collapsed ? 8 : '0 16px 0 22px',
            gap: 6,
          }
    }
  >
    <img src={logo} width={compact ? 36 : 30} height={compact ? 36 : 30} alt="Logo" />
    {compact ? (
      // Header ngang rộng nên để tên đầy đủ, không rút gọn như trong Sider.
      <div
        className="fw-600"
        style={{
          color: 'var(--main-color)',
          fontSize: 18,
          whiteSpace: 'nowrap',
        }}
      >
        Hệ thống quản lý văn bằng điện tử
      </div>
    ) : (
      !collapsed && (
        <div className="d-flex flex-column" style={{ minWidth: 0, gap: 1 }}>
          <div
            className="fw-600 line-height-1"
            style={{ color: 'var(--main-color)', fontSize: 15 }}
          >
            VBDT
          </div>
          {/* Tên hệ thống gói gọn 1 dòng; hẹp quá thì cắt bằng "…" chứ không xuống dòng. */}
          <div
            className="line-height-1"
            style={{
              color: 'var(--vbdt-color-text-secondary)',
              fontSize: 10,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Hệ thống quản lý văn bằng điện tử
          </div>
        </div>
      )
    )}
  </Link>
)

const LayoutAdmin = ({
  children,
  selectedKey = ['/'],
  menuAdmin = [],
  onClickMenu = () => {},
  breadcrumbItems = [],
  defaultCollapsed,
}: LayoutAdminProps) => {
  // Trạng thái thu gọn của Sider. Tôn trọng defaultCollapsed nếu được truyền.
  const [collapsemenu, setcollapsemenu] = useState(() => {
    if (defaultCollapsed !== undefined) return defaultCollapsed
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      process.env.NODE_ENV !== 'test'
    ) {
      return window.matchMedia('(min-width: 992px) and (max-width: 1199px)').matches
    }
    return false
  })
  const location = useLocation()
  const { settings, setSetting } = useLayoutSettings()
  const { theme } = useTheme()
  // Vùng thực sự cuộn: đổi theo Fixed Header / Fixed Sidebar (xem `scrollHost`).
  const scrollRef = useRef<HTMLDivElement>(null)

  // Màn hình lg: tự động thu gọn sidebar khi màn hình resize chuyển vào breakpoint lg (992px - 1199px)
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      process.env.NODE_ENV === 'test'
    )
      return
    const mql = window.matchMedia('(min-width: 992px) and (max-width: 1199px)')
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setcollapsemenu(true)
      }
    }
    mql.addEventListener?.('change', handler)
    return () => mql.removeEventListener?.('change', handler)
  }, [])

  const onChange = (menu: any) => {
    onClickMenu(menu)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Dùng location làm trigger chuyển trang
  useEffect(() => {
    const host = scrollRef.current ?? document.getElementById('body-admin-scroll')
    host?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [location])

  // Nhánh cấp 1 đang mở ở menu ngang. Suy từ trang hiện tại, nhưng cho phép bấm sang
  // nhánh khác (`mix` + `splitMenus`: nhánh cha không có path nên không tự đổi được).
  const derivedTopKey = useMemo(
    () => topKeyOfSelected(menuAdmin, selectedKey[0]),
    [menuAdmin, selectedKey]
  )
  const [pickedTopKey, setPickedTopKey] = useState<string>()
  // derivedTopKey la TRIGGER: doi trang thi bo lua chon nhanh cap 1 da bam tay.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dung lam trigger, khong phai deps thua
  useEffect(() => setPickedTopKey(undefined), [derivedTopKey])
  const activeTopKey = pickedTopKey ?? derivedTopKey

  const isTop = settings.layout === 'top'
  const isMix = settings.layout === 'mix'
  const isSide = !isTop && !isMix
  const hasMenu = menuAdmin.length > 0

  const siderItems = useMemo(
    () => (isMix && settings.splitMenus ? childrenOfActive(menuAdmin, activeTopKey) : menuAdmin),
    [isMix, settings.splitMenus, menuAdmin, activeTopKey]
  )

  const showSider =
    hasMenu &&
    settings.showMenu &&
    !isTop &&
    (!isMix || !settings.splitMenus || siderItems.length > 0)
  // Header ngang chạy hết chiều rộng, Sider nằm dưới nó — dấu hiệu nhận biết của `top`/`mix`.
  const headerOnTop = isTop || isMix

  /**
   * Menu ngang chỉ xuất hiện ở `top` (cả cây) và ở `mix` khi bật `splitMenus` (cấp 1).
   * `mix` không split thì antd pro chỉ để logo + tiêu đề trên header, menu vẫn nằm trọn ở Sider.
   */
  const horizontalItems = useMemo(() => {
    if (isTop) return menuAdmin
    if (isMix && settings.splitMenus) return topLevelItems(menuAdmin)
    return []
  }, [isTop, isMix, settings.splitMenus, menuAdmin])

  const siderBlock = showSider && (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsemenu}
      width={Math.max(260, settings.siderWidth || 260)}
      collapsedWidth={settings.siderCollapsedWidth}
      style={{
        background: 'var(--vbdt-color-bg-surface)',
        // Cố định: dính đầu vùng cuộn. Không cố định: đứng yên trong luồng, cuộn cùng nội dung.
        ...(settings.fixSiderbar
          ? {
              position: 'sticky' as const,
              top: 0,
              alignSelf: 'flex-start',
              height: isSide || (headerOnTop && !settings.fixedHeader) ? '100vh' : '100%',
            }
          : { position: 'static' as const }),
        borderRight: '1px solid var(--vbdt-color-border-subtle)',
        // Cuộn nằm ở tầng menu bên trong, không phải ở cả Sider.
        overflow: 'hidden',
      }}
    >
      {/*
        Ba tầng như Sider của antd pro: logo cố định trên, MENU là tầng DUY NHẤT cuộn,
        khối người dùng cố định dưới. Trước đây cả Sider cuộn nên logo trôi mất theo menu.
      */}
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Ở `top`/`mix` logo đã chuyển lên header, Sider không lặp lại. */}
        {isSide && settings.showMenuHeader && <LogoBlock collapsed={collapsemenu} />}

        <div
          className="sider-custom-scroll"
          style={{ flex: 1, minHeight: 0, overflow: 'hidden auto', paddingTop: isMix ? 8 : 0 }}
        >
          <UserMenuStyled
            $themedark={false}
            $collapsemenu={collapsemenu}
            style={{
              border: 'none',
              boxShadow: 'none',
              marginLeft: 0,
              width: '100%',
            }}
          >
            <div className={`side-bar-wrapper d-flex justify-content-space-between flex-column`}>
              <MenuLeft
                onChange={onChange}
                selectedKey={selectedKey}
                menu={siderItems}
                collapsemenu={collapsemenu}
                menuType={settings.siderMenuType}
              />
            </div>
          </UserMenuStyled>
        </div>

        {/* Chân trang đặt ở cuối Sider (khi bật showFooter) */}
        {settings.showFooter && (
          <footer
            style={{
              padding: collapsemenu ? '8px 4px' : '10px 12px',
              borderTop: '1px solid var(--vbdt-color-border-subtle)',
              textAlign: 'center',
              fontSize: 'var(--vbdt-font-size-xs, 11px)',
              lineHeight: '16px',
              color: 'var(--vbdt-color-text-tertiary)',
              flexShrink: 0,
              whiteSpace: collapsemenu ? 'nowrap' : 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title="© 2026 VBDT — Hệ thống quản lý văn bằng điện tử"
          >
            {collapsemenu ? '© VBDT' : '© 2026 VBDT — Quản lý tài liệu'}
          </footer>
        )}
      </div>
    </Sider>
  )

  /**
   * Ở `side` có Sider thì header không còn gì để chứa — logo, menu, breadcrumb, nút thu gọn
   * đều đã về Sider hoặc vùng nội dung; khối người dùng chuyển sang `topBarStrip`. Không vẽ
   * thanh rỗng, đúng như ảnh `side` của antd pro. Không có Sider (mode `top`, hoặc tắt Menu) thì header đón lại
   * khối người dùng để nó không mất chỗ.
   */
  const headerBlock = settings.showHeader && (headerOnTop || !showSider) && (
    <div
      style={{
        padding: '0 var(--vbdt-space-md)',
        minHeight: Math.max(56, settings.headerHeight || 64),
        height: Math.max(56, settings.headerHeight || 64),
        borderBottom: '1px solid var(--vbdt-color-border-default)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        background: 'var(--vbdt-color-bg-surface)',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        // `top`/`mix`: header là thanh tràn hết chiều rộng như pro — không lề, không bo góc.
        // Có lề 8px thì mép trái header lệch với Sider (Sider bám x=0), nhìn như gãy khối.
        ...(headerOnTop ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 } : {}),
        ...(settings.fixedHeader ? { position: 'sticky' as const, top: 0 } : {}),
      }}
    >
      {headerOnTop && settings.showMenuHeader && (
        <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
          <LogoBlock compact />
        </div>
      )}

      {horizontalItems.length > 0 && (
        <div style={{ flex: '1 1 auto', minWidth: 0, height: '100%' }}>
          <UiMenu
            mode="horizontal"
            theme={theme}
            items={horizontalItems}
            selectedKeys={activeTopKey ? [activeTopKey] : []}
            onClick={(info) => {
              setPickedTopKey(info.key)
              onChange(info)
            }}
            style={{
              borderBottom: 'none',
              height: '100%',
              lineHeight: `${settings.headerHeight}px`,
              background: 'transparent',
            }}
          />
        </div>
      )}

      {/* Header nào có mặt thì khối người dùng nằm bên phải nó, như `rightContentRender` của pro. */}
      <div
        style={{
          flex: '0 0 auto',
          minWidth: 0,
          marginLeft: 'auto',
        }}
      >
        <HeaderMain />
      </div>
    </div>
  )

  /**
   * Khối người dùng (avatar + thông báo + nút thiết lập bố cục) cần hiển thị ở topBarStrip
   * khi không có headerBlock phía trên (ví dụ: ở `side`, hoặc khi người dùng ẩn đầu trang `showHeader: false`).
   */
  const userHeaderInTopBar = !headerBlock

  /**
   * Thanh điều hướng đầu trang nội dung:
   * Chứa nút thu/phóng menu (nếu có Sider) + đường phân cách dọc + Breadcrumb + khối người dùng khi headerBlock ẩn.
   */
  const topBarStrip = (showSider ||
    (settings.showBreadcrumb && breadcrumbItems.length > 0) ||
    userHeaderInTopBar) && (
    <div
      style={{
        padding: '6px var(--vbdt-space-md) 2px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'transparent',
      }}
    >
      {showSider && (
        <UiTooltip title={collapsemenu ? 'Mở rộng menu' : 'Thu gọn menu'} placement="bottom">
          <Button
            type="text"
            onClick={() => setcollapsemenu(!collapsemenu)}
            aria-label={collapsemenu ? 'Mở rộng menu' : 'Thu gọn menu'}
            icon={collapsemenu ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            style={{
              width: 28,
              height: 28,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--vbdt-color-text-secondary)',
              padding: 0,
              marginLeft: -6,
            }}
          />
        </UiTooltip>
      )}

      {showSider && settings.showBreadcrumb && breadcrumbItems.length > 0 && (
        <div
          style={{
            width: 1,
            height: 14,
            backgroundColor: 'var(--vbdt-color-border-subtle)',
            flexShrink: 0,
          }}
        />
      )}

      {settings.showBreadcrumb && breadcrumbItems.length > 0 && (
        <UiBreadcrumb
          // ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
          className="breadcrumb-custom m-0"
          style={{ fontSize: 'var(--vbdt-font-size-sm)' }}
          items={breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1
            // Nhánh cha không có path (nhóm menu) thì không bấm được, giống pro.
            // Dùng LinkCusstom thay `href` để điều hướng trong SPA, không tải lại trang.
            return {
              title:
                item.path && !isLast ? (
                  <LinkCusstom to={item.path}>{item.label}</LinkCusstom>
                ) : (
                  item.label
                ),
            }
          })}
        />
      )}

      {/*
        Khi không có headerBlock (ví dụ `side`, hoặc khi tắt `showHeader`),
        khối người dùng (avatar + thông báo + nút thiết lập bố cục) chuyển lên góc phải thanh này.
      */}
      {userHeaderInTopBar && (
        <div style={{ marginLeft: 'auto' }}>
          <HeaderMain />
        </div>
      )}
    </div>
  )

  const contentBlock = children

  // Nội dung cuộn ở đâu: side → cả trang cuộn khi bỏ cố định Sider; top/mix → cả trang cuộn
  // khi bỏ cố định Header, ngược lại vùng dưới Header (Sider + nội dung) cuộn.
  const pageScrolls = headerOnTop ? !settings.fixedHeader : !settings.fixSiderbar

  // Content Width: "Cố định" giới hạn bề rộng nội dung và căn giữa; "Tràn" chiếm hết.
  const widthStyle: React.CSSProperties =
    settings.contentWidth === 'Fixed'
      ? { width: '100%', maxWidth: 'var(--vbdt-content-max-width)', margin: '0 auto' }
      : {}

  // Khi không có Sider (chế độ `top` hoặc tắt menu) thì footer hiển thị ở chân Content
  const footerBlock = !showSider && settings.showFooter && (
    <footer
      style={{
        padding: 'var(--vbdt-space-xs) var(--vbdt-space-md)',
        textAlign: 'center',
        fontSize: 'var(--vbdt-font-size-sm)',
        color: 'var(--vbdt-color-text-secondary)',
      }}
    >
      © 2026 VBDT — Hệ thống quản lý văn bằng điện tử
    </footer>
  )

  const openMenuButton = hasMenu && !settings.showMenu && !isTop && (
    <UiFloatButton
      style={{ left: 20 }}
      onClick={() => setSetting('showMenu', true)}
      tooltip={'Mở menu'}
      icon={<MenuUnfoldOutlined />}
    />
  )

  // `top` / `mix`: header full width ở trên, Sider + nội dung nằm trong hàng bên dưới.
  if (headerOnTop) {
    return (
      <UiLayout
        ref={pageScrolls ? scrollRef : undefined}
        style={{
          height: '100vh',
          background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
          display: 'flex',
          flexDirection: 'column',
          overflow: pageScrolls ? 'hidden auto' : 'hidden',
        }}
      >
        {headerBlock}
        {openMenuButton}
        <UiLayout
          ref={pageScrolls ? undefined : scrollRef}
          style={{
            flex: pageScrolls ? '1 0 auto' : 1,
            minHeight: 0,
            background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
            position: 'relative',
            overflow: pageScrolls ? 'visible' : 'hidden auto',
          }}
        >
          {siderBlock}
          <UiLayout
            style={{
              background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
              padding: 0,
              minWidth: 0,
            }}
          >
            <Content
              id="body-admin-scroll"
              className={collapsemenu ? 'sider-collapsed' : 'sider-expanded'}
              data-sider-collapsed={collapsemenu ? 'true' : 'false'}
              style={{
                background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', ...widthStyle }}>
                {topBarStrip}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {contentBlock}
                </div>
              </div>
              {footerBlock}
            </Content>
          </UiLayout>
        </UiLayout>
      </UiLayout>
    )
  }

  return (
    <UiLayout
      ref={pageScrolls ? scrollRef : undefined}
      style={{
        height: pageScrolls ? '100vh' : undefined,
        minHeight: '100vh',
        background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
        position: 'relative',
        overflow: pageScrolls ? 'hidden auto' : undefined,
      }}
    >
      {siderBlock}
      {openMenuButton}

      <UiLayout
        ref={pageScrolls ? undefined : scrollRef}
        style={{
          background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
          padding: 0,
          minWidth: 0,
          height: pageScrolls ? 'auto' : '100vh',
          overflow: pageScrolls ? 'visible' : 'hidden auto',
        }}
      >
        <Content
          id="body-admin-scroll"
          className={collapsemenu ? 'sider-collapsed' : 'sider-expanded'}
          data-sider-collapsed={collapsemenu ? 'true' : 'false'}
          style={{
            background: 'var(--vbdt-layout-bg, var(--vbdt-color-bg-page))',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', ...widthStyle }}>
            {/* Khối Header */}
            {headerBlock}

            {/* Khối Content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {topBarStrip}
              {contentBlock}
            </div>
          </div>
          {footerBlock}
        </Content>
      </UiLayout>
    </UiLayout>
  )
}

export default LayoutAdmin
